import { useEffect, useRef, type ReactNode } from "react";
import { useOffice } from "@/office/office-context";
import type { DocBlock } from "@/types/office";

type ToolProps = {
  label: string;
  hint: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
};

function ToolButton(props: ToolProps) {
  const state = props.disabled
    ? "cursor-not-allowed text-[#c3c7cc]"
    : props.active
      ? "bg-[#e8efff] text-[#2b62d9] ring-1 ring-[#c9dbff]"
      : "text-[#3f4a57] hover:bg-[#f1efe9]";
  return (
    <button
      type="button"
      title={props.hint}
      disabled={props.disabled}
      onClick={props.onClick}
      className={`flex h-7 min-w-7 items-center justify-center gap-1 rounded px-2 text-[13px] transition-colors ${state}`}
    >
      {props.children}
      <span className="sr-only">{props.label}</span>
    </button>
  );
}

function Divider() {
  return <span className="mx-1 h-4 w-px bg-[#e2ded4]" />;
}

export function DocEditor() {
  const office = useOffice();
  const doc = office.view.doc;
  const active = doc.blocks.find((block) => block.id === office.activeBlockId) ?? doc.blocks[0];
  const timer = useRef<number | null>(null);

  const live = !office.replaying;

  const scheduleCommit = (blockId: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => office.commitText(blockId), 900);
  };

  const addParagraphAfter = (blockId: string) => {
    office.commitText(blockId);
    const nextId = office.insertParagraph(blockId);
    window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-block-id="${nextId}"]`)?.focus();
    }, 0);
  };

  const wordCount = doc.blocks.reduce((total, block) => total + block.text.replace(/\s/g, "").length, 0);
  const tableCount = doc.blocks.filter((block) => block.type === "table").length;

  return (
    <section className="flex h-full min-h-0 flex-col bg-[#f4f2ec]">
      <div className="flex h-11 shrink-0 items-center gap-0.5 border-b border-[#e3ded2] bg-white px-3">
        {/* #region ui.format */}
        <ToolButton label="加粗" hint="加粗当前段落" disabled={!live || !active} active={active?.bold} onClick={() => active && office.toggleFormat(active.id, "bold")}>
          <span className="text-[15px] font-bold leading-none">B</span>
        </ToolButton>
        <ToolButton label="倾斜" hint="倾斜当前段落" disabled={!live || !active} active={active?.italic} onClick={() => active && office.toggleFormat(active.id, "italic")}>
          <span className="text-[15px] font-serif italic leading-none">I</span>
        </ToolButton>
        {/* #endregion ui.format */}
        <Divider />
        {/* #region ui.types */}
        <ToolButton label="标题 1" hint="设为一级标题" disabled={!live || !active} active={active?.type === "h1"} onClick={() => active && office.changeBlockType(active.id, "h1")}>
          <span className="text-[11px] font-bold tracking-tight">H1</span>
        </ToolButton>
        <ToolButton label="标题 2" hint="设为二级标题" disabled={!live || !active} active={active?.type === "h2"} onClick={() => active && office.changeBlockType(active.id, "h2")}>
          <span className="text-[11px] font-bold tracking-tight">H2</span>
        </ToolButton>
        <ToolButton label="正文" hint="设为正文段落" disabled={!live || !active} active={active?.type === "p"} onClick={() => active && office.changeBlockType(active.id, "p")}>
          <span className="text-[11px]">正文</span>
        </ToolButton>
        <ToolButton label="项目符号" hint="转为项目符号" disabled={!live || !active} active={active?.type === "li"} onClick={() => active && office.changeBlockType(active.id, "li")}>
          <span className="text-[13px]">• 列表</span>
        </ToolButton>
        {/* #endregion ui.types */}
        <Divider />
        {/* #region ui.table */}
        <ToolButton label="表格" hint="在下方插入 3×3 表格" disabled={!live || !active} onClick={() => active && office.insertTable(active.id)}>
          <span className="text-[11px]">▦ 表格</span>
        </ToolButton>
        {/* #endregion ui.table */}
        {/* #region ui.insert */}
        <ToolButton label="新建段落" hint="在下方新建空段落" disabled={!live || !active} onClick={() => active && addParagraphAfter(active.id)}>
          <span className="text-[11px]">＋ 段落</span>
        </ToolButton>
        {/* #endregion ui.insert */}
        <div className="ml-auto flex items-center gap-1">
          {/* #region ui.remove */}
          <ToolButton label="删除段落" hint="删除当前段落" disabled={!live || !active || doc.blocks.length <= 1} onClick={() => active && office.removeBlock(active.id)}>
            <span className="text-[11px] text-[#a4513f]">删除</span>
          </ToolButton>
          {/* #endregion ui.remove */}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
        <article className="mx-auto min-h-full w-full max-w-[720px] bg-white px-12 py-10 shadow-[0_1px_2px_rgba(31,35,40,0.08),0_12px_32px_-24px_rgba(31,35,40,0.45)]">
          <header className="mb-6 flex items-center justify-between border-b border-[#f0ede6] pb-3 text-[11px] text-[#9a9388]">
            <span>{doc.title}.wps</span>
            <span>{live ? "自动保存已开启" : "回放快照 · 只读"}</span>
          </header>

          {/* #region ui.blocks */}
          <div className="flex flex-col">
            {doc.blocks.map((block) => (
              <BlockView
                key={block.id}
                block={block}
                active={block.id === office.activeBlockId}
                readOnly={!live}
                onFocus={() => office.selectBlock(block.id)}
                onChange={(text) => {
                  office.patchText(block.id, text);
                  scheduleCommit(block.id);
                }}
                onCommit={() => office.commitText(block.id)}
                onEnter={() => addParagraphAfter(block.id)}
              />
            ))}
          </div>
          {/* #endregion ui.blocks */}

          <p className="mt-8 text-[11px] text-[#c3bdb1]">点击段落即可选中，工具栏只作用于选中段落。</p>
        </article>
      </div>

      <div className="flex h-8 shrink-0 items-center gap-4 border-t border-[#e3ded2] bg-[#fbfaf7] px-4 text-[11px] text-[#8d8679]">
        <span>字数 {wordCount}</span>
        <span>段落 {doc.blocks.filter((block) => block.type !== "table").length}</span>
        <span>表格 {tableCount}</span>
        <span className="ml-auto">{live ? "输入后 0.9 秒自动记录为一步" : "回放模式：左侧为第 " + String(office.activeStep?.index ?? 0) + " 步的状态"}</span>
      </div>
    </section>
  );
}

type BlockViewProps = {
  block: DocBlock;
  active: boolean;
  readOnly: boolean;
  onFocus: () => void;
  onChange: (text: string) => void;
  onCommit: () => void;
  onEnter: () => void;
};

function BlockView(props: BlockViewProps) {
  const { block } = props;
  const ref = useRef<HTMLDivElement>(null);

  // 输入时 React 不接管文本节点，避免光标被重置；只在外部状态变化（回放/切换）时同步。
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const domText = node.innerText.replace(/\n$/, "");
    if (domText !== block.text) node.innerText = block.text;
  }, [block.text]);

  if (block.type === "table") {
    return (
      <div
        onClick={props.onFocus}
        className={`my-4 overflow-hidden rounded border ${props.active ? "border-[#4f8cff] ring-2 ring-[#4f8cff]/15" : "border-[#e6e2d8]"}`}
      >
        <table className="w-full border-collapse text-[13px]">
          <tbody>
            {block.rows?.map((row, rowIndex) => (
              <tr key={rowIndex} className={rowIndex === 0 ? "bg-[#f7f5f0]" : "bg-white"}>
                {row.map((cell, cellIndex) => (
                  <td
                    key={cellIndex}
                    className={`border-b border-r border-[#ece8de] px-3 py-2 text-[#333a42] last:border-r-0 ${rowIndex === 0 ? "font-medium text-[#6b6355]" : ""}`}
                  >
                    {cell || " "}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const base = "outline-none whitespace-pre-wrap break-words border-l-2 pl-3 -ml-3 transition-colors";
  const activeCls = props.active ? "border-[#4f8cff]" : "border-transparent hover:border-[#e0dbcf]";
  const typeCls =
    block.type === "h1"
      ? "mb-5 text-[26px] font-semibold leading-9 tracking-tight text-[#1d232b]"
      : block.type === "h2"
        ? "mb-2 mt-5 text-[18px] font-semibold leading-7 text-[#1d232b]"
        : block.type === "li"
          ? "mb-1 flex text-[14.5px] leading-7 text-[#333a42]"
          : "mb-2 text-[14.5px] leading-7 text-[#333a42]";

  const bold = block.bold ? "font-semibold" : "";
  const italic = block.italic ? "italic" : "";

  const editable = (
    <div
      ref={ref}
      data-block-id={block.id}
      data-block-type={block.type}
      role="textbox"
      tabIndex={0}
      suppressContentEditableWarning
      contentEditable={!props.readOnly}
      spellCheck={false}
      onFocus={props.onFocus}
      onInput={(event) => props.onChange(event.currentTarget.innerText.replace(/\n$/, ""))}
      onBlur={props.onCommit}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          props.onEnter();
        }
      }}
      className={`${base} ${activeCls} ${typeCls} ${bold} ${italic} empty:before:text-[#b8b2a4] empty:before:content-['输入正文…']`}
    />
  );

  if (block.type !== "li") return editable;

  return (
    <div className="flex items-start">
      <span className="mr-2 mt-0.5 select-none text-[#9a9388]">•</span>
      {editable}
    </div>
  );
}
